import {
  forwardRef,
  useEffect,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  type ThHTMLAttributes,
} from "react";
import { AlertTriangle, ChevronDown, Inbox, Loader2 } from "lucide-react";
import { friendlyMessage } from "@/api/errors";
import { cn, eventTime, formatDateTime, timeAgo } from "@/lib/format";

// ───────────── Button ─────────────

type Variant = "primary" | "secondary" | "ghost" | "danger" | "warning" | "success";
type Size = "sm" | "md" | "lg" | "xl";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-primary text-primary-fg hover:bg-primary-hover",
  secondary: "bg-surface text-fg border border-border hover:bg-surface-2",
  ghost: "text-fg hover:bg-surface-2",
  danger: "bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-500",
  warning: "bg-amber-500 text-black hover:bg-amber-400",
  success: "bg-green-600 text-white hover:bg-green-700",
};
const SIZES: Record<Size, string> = {
  sm: "h-8 px-2.5 text-sm gap-1.5",
  md: "h-10 px-3.5 text-sm gap-2",
  lg: "h-12 px-5 text-base gap-2",
  xl: "h-16 px-6 text-lg gap-3",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", loading, icon, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center rounded-md font-medium transition-colors select-none",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

// ───────────── Badge ─────────────

export type Tone = "neutral" | "info" | "success" | "warning" | "danger" | "critical" | "muted" | "primary";

const TONES: Record<Tone, string> = {
  neutral: "bg-surface-3 text-fg",
  muted: "bg-surface-2 text-subtle",
  info: "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200",
  primary: "bg-cyan-100 text-cyan-900 dark:bg-cyan-950 dark:text-cyan-200",
  success: "bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-200",
  warning: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
  danger: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200",
  critical: "bg-red-600 text-white dark:bg-red-600",
};

export function Badge({ tone = "neutral", children, className, dot, title }: { tone?: Tone; children: ReactNode; className?: string; dot?: boolean; title?: string }) {
  return (
    <span title={title} className={cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap", TONES[tone], className)}>
      {dot && <span className="size-1.5 rounded-full bg-current opacity-80" aria-hidden />}
      {children}
    </span>
  );
}

// ───────────── Card ─────────────

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={cn("rounded-lg border border-border bg-surface", className)}>{children}</section>;
}

export function CardHeader({ title, subtitle, actions, icon }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; icon?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
      <div className="flex min-w-0 items-start gap-2">
        {icon && <span className="mt-0.5 text-subtle">{icon}</span>}
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">{title}</h2>
          {subtitle && <p className="text-xs text-subtle">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function CardBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("p-4", className)}>{children}</div>;
}

// ───────────── Form controls ─────────────

const controlBase =
  "w-full rounded-md border border-border bg-surface px-3 text-sm text-fg placeholder:text-subtle focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-60";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cn(controlBase, "h-10", className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cn(controlBase, "min-h-20 py-2", className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return (
    <div className="relative">
      <select ref={ref} className={cn(controlBase, "h-10 appearance-none pr-8", className)} {...rest}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-subtle" aria-hidden />
    </div>
  );
});

export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: (id: string) => ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <label htmlFor={id} className="text-xs font-medium text-muted">
        {label}
      </label>
      {children(id)}
      {error ? <p className="text-xs text-danger">{error}</p> : hint ? <p className="text-xs text-subtle">{hint}</p> : null}
    </div>
  );
}

export function Checkbox({ label, checked, onChange, disabled, hint }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; hint?: ReactNode }) {
  return (
    <label className={cn("flex items-start gap-2 text-sm", disabled && "opacity-60")}>
      <input type="checkbox" className="mt-0.5 size-4 accent-[var(--primary)]" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {hint && <span className="block text-xs text-subtle">{hint}</span>}
      </span>
    </label>
  );
}

/** Number input that keeps a local text buffer so partial input ("-", "1.") isn't clobbered. */
export function NumberInput({
  value,
  onChange,
  step,
  min,
  max,
  allowNull,
  className,
  ...rest
}: {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  step?: number;
  min?: number;
  max?: number;
  allowNull?: boolean;
  className?: string;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  "aria-label"?: string;
}) {
  const [text, setText] = useState(value === null || value === undefined ? "" : String(value));
  useEffect(() => {
    const parsed = text.trim() === "" ? null : Number(text);
    if (parsed !== (value ?? null)) setText(value === null || value === undefined ? "" : String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <Input
      {...rest}
      type="number"
      inputMode="decimal"
      step={step ?? "any"}
      min={min}
      max={max}
      className={className}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        if (e.target.value.trim() === "") {
          if (allowNull) onChange(null);
          return;
        }
        const n = Number(e.target.value);
        if (Number.isFinite(n)) onChange(n);
      }}
    />
  );
}

// ───────────── Feedback ─────────────

export function Spinner({ className, label }: { className?: string; label?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-2 text-sm text-subtle", className)} role="status">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {label ?? "Loading…"}
    </span>
  );
}

export function PageLoader() {
  return (
    <div className="flex h-48 items-center justify-center">
      <Spinner />
    </div>
  );
}

export function EmptyState({ title, children, icon, action }: { title: ReactNode; children?: ReactNode; icon?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
      <span className="text-subtle">{icon ?? <Inbox className="size-8" aria-hidden />}</span>
      <p className="font-medium">{title}</p>
      {children && <div className="max-w-md text-sm text-subtle">{children}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, compact }: { error: unknown; onRetry?: () => void; compact?: boolean }) {
  return (
    <div className={cn("flex items-start gap-3 rounded-md border border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/50 dark:text-red-200", compact ? "p-2 text-sm" : "p-4")}>
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="flex-1">
        <p>{friendlyMessage(error)}</p>
      </div>
      {onRetry && (
        <Button size="sm" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

export function Banner({ tone, title, children, icon, actions }: { tone: "info" | "warning" | "danger" | "success"; title: ReactNode; children?: ReactNode; icon?: ReactNode; actions?: ReactNode }) {
  const tones = {
    info: "border-sky-300 bg-sky-50 text-sky-950 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-100",
    warning: "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100",
    danger: "border-red-300 bg-red-50 text-red-950 dark:border-red-900 dark:bg-red-950/50 dark:text-red-100",
    success: "border-green-300 bg-green-50 text-green-950 dark:border-green-900 dark:bg-green-950/40 dark:text-green-100",
  } as const;
  return (
    // Wraps: on a narrow screen the actions drop below the text instead of squeezing it.
    <div role={tone === "danger" ? "alert" : "status"} className={cn("flex flex-wrap items-start gap-3 rounded-lg border px-4 py-3", tones[tone])}>
      {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-[12rem] flex-1">
        <p className="text-sm font-semibold">{title}</p>
        {children && <div className="mt-0.5 text-sm opacity-90">{children}</div>}
      </div>
      {actions && <div className="flex shrink-0 gap-2">{actions}</div>}
    </div>
  );
}

// ───────────── Data display ─────────────

export function KeyValue({ items, className }: { items: Array<[ReactNode, ReactNode]>; className?: string }) {
  return (
    <dl className={cn("grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-1.5 text-sm", className)}>
      {items.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="text-subtle">{k}</dt>
          <dd className="min-w-0 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function TimeAgo({ iso, now }: { iso: string | null | undefined; now?: number }) {
  return (
    <time dateTime={iso ?? undefined} title={formatDateTime(iso)}>
      {timeAgo(iso, now)}
    </time>
  );
}

/**
 * When a robot-reported event happened. Falls back to the platform's receipt time, and says so,
 * when the robot's clock is implausible (simulator time, unset clock).
 */
export function EventTimeAgo({ robotIso, platformIso, now, verb = "raised" }: { robotIso: string | null | undefined; platformIso: string | null | undefined; now?: number; verb?: string }) {
  const t = eventTime(robotIso, platformIso);
  if (t.source === "robot") {
    return (
      <>
        {verb}{" "}
        <time dateTime={t.iso ?? undefined} title={`Robot clock: ${formatDateTime(t.iso)}${platformIso ? ` · first seen by the platform ${formatDateTime(platformIso)}` : ""}`}>
          {timeAgo(t.iso, now)}
        </time>
      </>
    );
  }
  return (
    <>
      first seen{" "}
      <time dateTime={t.iso ?? undefined} title={`Platform clock: ${formatDateTime(t.iso)}${robotIso ? ` · the robot reported ${robotIso}, which is not a usable time` : ""}`}>
        {timeAgo(t.iso, now)}
      </time>
      {t.robotClockInvalid && <span className="text-subtle"> (robot timestamp unavailable)</span>}
    </>
  );
}

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("overflow-x-auto", className)}>
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}
export function Th({ children, className, ...rest }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th className={cn("border-b border-border px-3 py-2 text-left text-xs font-semibold tracking-wide text-subtle uppercase", className)} {...rest}>
      {children}
    </th>
  );
}
export function Td({ children, className, colSpan }: { children?: ReactNode; className?: string; colSpan?: number }) {
  return (
    <td colSpan={colSpan} className={cn("border-b border-border px-3 py-2 align-middle", className)}>
      {children}
    </td>
  );
}

export function JsonView({ value, className }: { value: unknown; className?: string }) {
  return (
    <pre className={cn("max-h-80 overflow-auto rounded-md bg-surface-2 p-3 font-mono text-xs leading-relaxed", className)}>
      {value === undefined ? "—" : JSON.stringify(value, null, 2)}
    </pre>
  );
}

export function ProgressBar({ value, tone = "primary", className }: { value: number | null | undefined; tone?: "primary" | "success" | "danger" | "warning"; className?: string }) {
  const pct = value === null || value === undefined ? null : Math.max(0, Math.min(1, value > 1 ? value / 100 : value)) * 100;
  const color = { primary: "bg-primary", success: "bg-green-600", danger: "bg-red-600", warning: "bg-amber-500" }[tone];
  return (
    <div className={cn("h-2 w-full overflow-hidden rounded-full bg-surface-3", className)} role="progressbar" aria-valuenow={pct ?? undefined} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn("h-full rounded-full transition-[width]", color, pct === null && "w-1/3 animate-pulse")} style={pct === null ? undefined : { width: `${pct}%` }} />
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, back }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        {back}
        <h1 className="truncate text-xl font-semibold">{title}</h1>
        {subtitle && <div className="mt-0.5 text-sm text-subtle">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Progressive disclosure: advanced sections are collapsed by default. */
export function Disclosure({ title, children, defaultOpen = false, subtitle }: { title: ReactNode; children: ReactNode; defaultOpen?: boolean; subtitle?: ReactNode }) {
  return (
    <details className="group rounded-lg border border-border bg-surface" open={defaultOpen}>
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 select-none">
        <span>
          <span className="text-sm font-semibold">{title}</span>
          {subtitle && <span className="block text-xs text-subtle">{subtitle}</span>}
        </span>
        <ChevronDown className="size-4 text-subtle transition-transform group-open:rotate-180" aria-hidden />
      </summary>
      <div className="border-t border-border p-4">{children}</div>
    </details>
  );
}
